import storageRows from './data/phoneModelStorages.json'
import { normalizeModelLabel } from './phoneModelMatch'
import { parseStorageLabelToGb } from './storageGb'

export interface PhoneModelStorageRow {
  manufacturer: string
  model_name: string
  storage_gb: number[]
}

function normalizeManufacturer(name: string): string {
  return name.trim().toLowerCase()
}

function catalogKey(manufacturer: string, modelName: string): string {
  return `${normalizeManufacturer(manufacturer)}|${normalizeModelLabel(modelName)}`
}

const STORAGE_GB_BY_KEY = new Map<string, number[]>()
for (const row of storageRows as PhoneModelStorageRow[]) {
  const key = catalogKey(row.manufacturer, row.model_name)
  const tiers = (row.storage_gb || [])
    .map((gb) => Number(gb))
    .filter((gb) => Number.isFinite(gb) && gb > 0)
  if (!tiers.length) continue
  STORAGE_GB_BY_KEY.set(key, [...new Set(tiers)].sort((a, b) => a - b))
}

/** Format gigabytes as sell-form storage label (e.g. 1024 → "1TB"). */
export function formatStorageGbLabel(gb: number): string {
  if (gb >= 1024 && gb % 1024 === 0) {
    return `${gb / 1024}TB`
  }
  return `${gb}GB`
}

function lookupAdminHintsStorageGb(modelSpecsJson: string | null | undefined): number[] | null {
  if (!modelSpecsJson?.trim()) return null
  try {
    const raw = JSON.parse(modelSpecsJson) as unknown
    if (!raw || typeof raw !== 'object') return null
    const hints = (raw as { admin_hints?: { storage_gb?: unknown } }).admin_hints
    const tiers = hints?.storage_gb
    if (!Array.isArray(tiers)) return null
    const cleaned = tiers
      .map((gb) => Number(gb))
      .filter((gb) => Number.isFinite(gb) && gb > 0)
    if (!cleaned.length) return null
    return [...new Set(cleaned)].sort((a, b) => a - b)
  } catch {
    return null
  }
}

/** Lookup known storage tiers (GB) from curated market-price CSV export. */
export function lookupPhoneModelStorageGb(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  modelSpecsJson?: string | null,
): number[] | null {
  const fromAdmin = lookupAdminHintsStorageGb(modelSpecsJson)
  if (fromAdmin?.length) return fromAdmin
  const mfr = (manufacturer ?? '').trim()
  const model = (modelName ?? '').trim()
  if (!mfr || !model) return null
  return STORAGE_GB_BY_KEY.get(catalogKey(mfr, model)) ?? null
}

/** Minimum storage tier for a model (like launch year floor). */
export function resolvePhoneModelMinStorageGb(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  modelSpecsJson?: string | null,
): number | null {
  const tiers = lookupPhoneModelStorageGb(manufacturer, modelName, modelSpecsJson)
  if (!tiers?.length) return null
  return tiers[0]
}

/** Storage labels >= model minimum, largest-first; falls back when model is unknown. */
export function resolvePhoneModelStorageLabels(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  fallbackLabels: readonly string[] = [],
  modelSpecsJson?: string | null,
): readonly string[] {
  const minGb = resolvePhoneModelMinStorageGb(manufacturer, modelName, modelSpecsJson)
  if (minGb == null) return fallbackLabels
  return fallbackLabels.filter((label) => {
    const gb = parseStorageLabelToGb(label)
    return gb != null && gb >= minGb
  })
}

/** Default storage when a catalog model is picked (minimum known tier). */
export function defaultListingStorageForModel(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  modelSpecsJson?: string | null,
): string | null {
  const minGb = resolvePhoneModelMinStorageGb(manufacturer, modelName, modelSpecsJson)
  if (minGb == null) return null
  return formatStorageGbLabel(minGb)
}

export function isStorageAllowedForModel(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  storageLabel: string,
  parseGb: (label: string) => number | undefined = parseStorageLabelToGb,
  modelSpecsJson?: string | null,
): boolean {
  const minGb = resolvePhoneModelMinStorageGb(manufacturer, modelName, modelSpecsJson)
  if (minGb == null) return true
  const gb = parseGb(storageLabel)
  if (gb == null) return false
  return gb >= minGb
}
